// src/pages/Dynamics365EcoProtector.jsx
import FreightCalculationDisplayModelView from "../QuotePricePage/FreightCalculationDisplayModelView";

import { useState, useEffect, useCallback, useContext, useRef } from "react";
import {
    Container,
    Paper,
    Select,
    MenuItem,
    TextField,
    Button,
    Grid,
    Typography,
    Box,
    FormControl,
    InputLabel,
    CircularProgress,
    Snackbar,
    Alert,
    Fade,
    Card,
    CardContent,
    Divider,
    Stepper,
    Step,
    StepLabel,
} from "@mui/material";
import apiClient from "../../Helpers/apiClient";
import { useLocation, useNavigate } from "react-router-dom";
import AuthContext from "../../Helpers/auth-context";
import Dynamics365NavigationBar from "./Dynamics365NavigationBar";
import useAutoLogout from "../../Helpers/useAutoLogout";
import { useQuote } from "./useQuote";
import BackToCPQButton from "../../Components/backToCPQButton";
import { useEcoProtectorState, WORKFLOW_STAGES } from "../../Hooks/useEcoProtectorState";
import AddressAutocomplete from "../../Components/AddressAutocomplete";

import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";

/* ------------------------------------------------------------------ */
/*  PRICE BREAKDOWN & DOWNLOAD BUTTONS (unchanged)                     */
/* ------------------------------------------------------------------ */
const PriceBreakdown = ({ priceData }) => {
    if (!priceData) return null;

    const items = [
        { label: "Tank Base", value: priceData.tankPrice },
        { label: "Riser", value: priceData.riserPrice },
        { label: "Inlet Connection", value: priceData.inletPrice },
        { label: "Outlet Connection", value: priceData.outletPrice },
        { label: "Cover", value: priceData.coverPrice },
        { label: "Freight", value: priceData.freightPrice },
        { label: "Total Price", value: priceData.totalPrice },
    ];

    return (
        <Card sx={{ mt: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Price Breakdown
                </Typography>
                {items.map(({ label, value }) => (
                    <Box key={label} sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="body2">{label}:</Typography>
                        <Typography variant="body2">${value?.toFixed(2) || "0.00"}</Typography>
                    </Box>
                ))}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="h6" color="primary">
                        Total:
                    </Typography>
                    <Typography variant="h6" color="primary">
                        ${priceData.totalPrice?.toFixed(2) || "0.00"}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

const DownloadButtons = ({ urls, onDownload, disabled }) => (
    <Box>
        {Object.entries(urls).map(([type, url]) => (
            <Button
                key={type}
                fullWidth
                variant="outlined"
                onClick={() => onDownload(url, type)}
                disabled={disabled || !url}
                sx={{ mt: 1 }}
            >
                Download {type.toUpperCase()}
            </Button>
        ))}
    </Box>
);

/* ------------------------------------------------------------------ */
/*  MAIN COMPONENT                                                    */
/* ------------------------------------------------------------------ */
const Dynamics365EcoProtector1 = () => {
    /* ----------------------  STATE & HOOKS  ----------------------- */
    const { state } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const { addProduct } = useQuote();

    const [QuoteId, setQuoteId] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
    const [isAddingToQuote, setIsAddingToQuote] = useState(false);
    const [coverHeight, setCoverHeight] = useState(100);
    const [connectionSizes, setConnectionSizes] = useState([]);
    const [connectionTypes, setConnectionTypes] = useState([]);
    const [tankInvertStandard, setTankInvertStandard] = useState(0);
    const [errors, setErrors] = useState({ fslAndInletIL: "", riser: "" });
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [freightCalculationResult, setFreightCalculationResult] = useState(null);

    const showNotification = useCallback((message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const {
        currentStage,
        activeStep,
        isGenerating,
        totalPrice,
        priceBreakdown,
        downloadUrls,
        modelConfig,
        setIsGenerating,
        setTotalPrice,
        setPriceBreakdown,
        setModelConfig,
        moveToGenerateStage,
        moveToDownloadStage,
        resetWorkflow,
        restoreStateFromLocalStorage,
        saveStateToLocalStorage,
    } = useEcoProtectorState(QuoteId, quantity, showNotification);

    /* ----------------------  THREE.JS REFS  ----------------------- */
    const mountRef = useRef(null);
    const sceneRef = useRef(null);
    const rendererRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);
    const animationIdRef = useRef(null);
    const modelRef = useRef(null); // current GLTF group
    const glbBlobRef = useRef(null); // keep the latest blob for download

    /* ----------------------  CONSTANTS  -------------------------- */
    const modelTypes = ["ECP.1200", "ECP.1500", "ECP.1850", "ECP.2200", "ECP.2500", "ECP.3000", "ECP.3500"];
    const coverTypes = ["Class B", "Class D", "Class E"];
    const STAGE_LABELS = ["Calculate Price", "Generate Model", "Download & Quote"];

    useAutoLogout({ isAuthenticated: state.isAuthenticated });

    /* ----------------------  HELPERS  ---------------------------- */
    const isStage = (stage) => currentStage === stage;
    const canInteractWithInputs = () => isStage(WORKFLOW_STAGES.CALCULATE_PRICE) && !isCalculatingPrice;
    const canCalculatePrice = () => isStage(WORKFLOW_STAGES.CALCULATE_PRICE) && !isCalculatingPrice;
    const canGenerateModel = () => isStage(WORKFLOW_STAGES.GENERATE_MODEL) && !isGenerating && totalPrice > 0;
    const canDownloadAndQuote = () => isStage(WORKFLOW_STAGES.DOWNLOAD_AND_QUOTE);

    /* ----------------------  THREE.JS SETUP  --------------------- */
    useEffect(() => {
        if (!mountRef.current) return;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf5f5f5);
        sceneRef.current = scene;

        const camera = new THREE.PerspectiveCamera(
            75,
            mountRef.current.clientWidth / mountRef.current.clientHeight,
            0.1,
            1000
        );
        cameraRef.current = camera;

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.2;
        mountRef.current.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // lights
        scene.add(new THREE.AmbientLight(0xffffff, 0.8));
        const dir = new THREE.DirectionalLight(0xffffff, 2);
        dir.position.set(10, 15, 10);
        dir.castShadow = true;
        dir.shadow.mapSize.set(2048, 2048);
        scene.add(dir);
        const fill = new THREE.DirectionalLight(0xffffff, 0.6);
        fill.position.set(-10, 5, -10);
        scene.add(fill);

        // HDR environment
        new RGBELoader().setPath("/hdr/").load("studio_small_03_1k.hdr", (tex) => {
            tex.mapping = THREE.EquirectangularReflectionMapping;
            scene.environment = tex;
            scene.background = tex;
        });

        // controls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controlsRef.current = controls;

        camera.position.set(5, 5, 5);
        controls.update();

        const animate = () => {
            animationIdRef.current = requestAnimationFrame(animate);
            controls.update();
            renderer.render(scene, camera);
        };
        animate();

        return () => {
            cancelAnimationFrame(animationIdRef.current);
            if (mountRef.current && renderer.domElement) {
                mountRef.current.removeChild(renderer.domElement);
            }
            renderer.dispose();
        };
    }, []);

    // resize handler
    useEffect(() => {
        const onResize = () => {
            if (!mountRef.current || !rendererRef.current || !cameraRef.current) return;
            const { clientWidth, clientHeight } = mountRef.current;
            cameraRef.current.aspect = clientWidth / clientHeight;
            cameraRef.current.updateProjectionMatrix();
            rendererRef.current.setSize(clientWidth, clientHeight);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    /* ----------------------  GLTF HELPERS  ----------------------- */
    const clearPreviousModel = useCallback(() => {
        if (modelRef.current) {
            modelRef.current.traverse((child) => {
                if (child.isMesh) {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) {
                        if (Array.isArray(child.material)) child.material.forEach((m) => m.dispose());
                        else child.material.dispose();
                    }
                }
            });
            sceneRef.current?.remove(modelRef.current);
            modelRef.current = null;
        }
    }, []);

    const loadGLTF = useCallback(
        async (glbBlob) => {
            if (!glbBlob || !sceneRef.current) return;
            clearPreviousModel();

            try {
                const loader = new GLTFLoader();
                const buffer = await glbBlob.arrayBuffer();
                const gltf = await loader.parseAsync(buffer, "");
                const model = gltf.scene;

                model.rotation.x = -Math.PI / 2;
                model.traverse((c) => {
                    if (c.isMesh) {
                        c.geometry.computeVertexNormals();
                        c.material.flatShading = false;
                        c.castShadow = true;
                        c.receiveShadow = true;
                        c.material.metalness = 0.7;
                        c.material.roughness = 0.3;
                        c.material.envMapIntensity = 2.0;
                        c.material.needsUpdate = true;
                    }
                });

                const box = new THREE.Box3().setFromObject(model);
                const center = box.getCenter(new THREE.Vector3());
                const size = box.getSize(new THREE.Vector3());
                const maxDim = Math.max(size.x, size.y, size.z);
                const scale = 5 / maxDim;

                model.position.sub(center.multiplyScalar(scale));
                model.scale.set(scale, scale, scale);
                sceneRef.current.add(model);
                modelRef.current = model;

                // camera focus
                const radius = (maxDim * scale) / 2;
                const distance = radius * 2.2;
                const dir = new THREE.Vector3(1, 0.8, 1).normalize();
                cameraRef.current.position.copy(dir.multiplyScalar(distance));
                controlsRef.current.target.set(0, 0, 0);
                controlsRef.current.update();
            } catch (e) {
                console.error(e);
                showNotification("Failed to load 3D model", "error");
            }
        },
        [clearPreviousModel, showNotification]
    );

    /* ----------------------  CONNECTION LOADING  ---------------- */
    const loadConnectionSizes = useCallback(
        async (modelType) => {
            try {
                const { data } = await apiClient.post("/api/model/connectionsizes", modelType, {
                    headers: { "Content-Type": "application/json" },
                });
                if (data?.item1) {
                    setConnectionSizes(data.item1);
                    setTankInvertStandard(data.item2);
                    if (data.item1.length && !modelConfig.connectionSize) {
                        const first = data.item1[0];
                        setModelConfig((p) => ({ ...p, connectionSize: first }));
                        await loadConnectionTypes(first);
                    }
                }
            } catch {
                showNotification("Failed to load connection sizes", "error");
            }
        },
        [modelConfig.connectionSize, setModelConfig, showNotification]
    );

    const loadConnectionTypes = useCallback(
        async (size) => {
            try {
                const { data } = await apiClient.post("/api/model/connectiontypes", size, {
                    headers: { "Content-Type": "application/json" },
                });
                if (Array.isArray(data)) {
                    setConnectionTypes(data);
                    if (data.length && !modelConfig.connectionType) {
                        setModelConfig((p) => ({ ...p, connectionType: data[0] }));
                    }
                }
            } catch {
                showNotification("Failed to load connection types", "error");
            }
        },
        [modelConfig.connectionType, setModelConfig, showNotification]
    );

    /* ----------------------  PRICE CALCULATION  ----------------- */
    const calculatePrice = useCallback(async () => {
        const { finishSurfaceLevel, inletIL, connectionSize, connectionType, coverType, city, modelType } =
            modelConfig;

        const newErrors = { fslAndInletIL: "", riser: "" };
        if (isNaN(finishSurfaceLevel) || isNaN(inletIL)) {
            newErrors.fslAndInletIL = "FSL and Inlet IL are required";
        } else if ((finishSurfaceLevel - inletIL).toFixed(2) < tankInvertStandard) {
            newErrors.fslAndInletIL = `FSL-Inlet must be ≥ ${tankInvertStandard}m`;
        }
        if (!connectionSize || !connectionType) {
            newErrors.fslAndInletIL = "Select connection size & type";
        }
        setErrors(newErrors);
        if (newErrors.fslAndInletIL) return;

        setIsCalculatingPrice(true);
        try {
            const payload = {
                Model: modelType,
                FinishSurfaceLevel: finishSurfaceLevel,
                InletIL: inletIL,
                InletSize: parseInt(connectionSize),
                OutletSize: parseInt(connectionSize),
                ConnectionType: connectionType,
                CoverType: coverType,
                FreightLocation: city,
            };
            const { data } = await apiClient.post("/api/price/calculation", payload, {
                headers: { "Content-Type": "application/json" },
            });

            setTotalPrice(data.totalPrice);
            setCoverHeight(data.coverHeight);
            setPriceBreakdown(data);
            setFreightCalculationResult(data.freightResult || null);

            const riserMm = parseFloat(data.riser) * 1000;
            const riserHeight = riserMm < 100 ? 100 : riserMm;
            setModelConfig((p) => ({ ...p, riserHeight }));
            setErrors((e) => ({ ...e, riser: riserMm < 100 ? "Riser ≥ 100 mm" : "" }));

            showNotification("Price calculated – you can now generate the model.");
            moveToGenerateStage();
        } catch {
            showNotification("Price calculation failed", "error");
        } finally {
            setIsCalculatingPrice(false);
        }
    }, [
        modelConfig,
        tankInvertStandard,
        showNotification,
        moveToGenerateStage,
        setTotalPrice,
        setPriceBreakdown,
        setModelConfig,
    ]);

    /* ----------------------  HANDLE UPDATE (polling)  ----------- */
    const handleUpdate = useCallback(async () => {
        // ---- validation (same as EcoProtectorConfigurator) ----
        const newErrors = {};
        if (modelConfig.finishSurfaceLevel <= modelConfig.inletIL) {
            newErrors.fslAndInletIL = "FSL must be > Inlet IL";
        }
        if (!modelConfig.connectionSize) newErrors.connectionSize = "Select size";
        if (!modelConfig.connectionType) newErrors.connectionType = "Select type";
        setErrors((e) => ({ ...e, ...newErrors }));
        if (Object.keys(newErrors).length) {
            showNotification("Fix validation errors", "error");
            return;
        }

        setIsGenerating(true);
        saveStateToLocalStorage(); // persist config
        clearPreviousModel();

        try {
            const diameter = parseInt(modelConfig.modelType.split(".")[1]);
            const data = {
                browserConnectionId: "IamADummy", // kept for API compatibility
                params: {
                    Diameter: diameter.toString(),
                    FSL: (modelConfig.finishSurfaceLevel * 1000).toString(),
                    InvertIL: (modelConfig.inletIL * 1000).toString(),
                    InletDiameter: modelConfig.connectionSize || "150",
                    InletType: modelConfig.connectionType,
                    LidThickness: coverHeight.toString(),
                },
            };

            // 1. update Onshape variables + toggle features
            await apiClient.post("/api/onshape/update-variables", data);
            const response = await apiClient.post("/api/onshape/toggle-features", data);

            // Extract values from the returned JSON
            const { workItemId, workItemIdPath, message } = response.data;

            setModelConfig((prev) => ({ ...prev, workItemId }));
            saveStateToLocalStorage(); // THIS LINE WAS MISSING!

            console.log("Work item ID:", workItemId);
            console.log("Work item path:", workItemIdPath);

            // 2. request GLTF export
            const exportRes = await apiClient.post("/api/onshape/export-gltf");
            const translationId = exportRes.data.translationId;

            // 3. poll until DONE (max 30 attempts, 1.5 s each)
            let glbBlob = null;
            for (let i = 0; i < 30; i++) {
                await new Promise((r) => setTimeout(r, 1500));
                const status = await apiClient.get(`/api/onshape/translation-status/${translationId}`);
                if (status.data.requestState === "DONE") {
                    const resultId = status.data.resultElementIds[0];
                    const dl = await apiClient.get("/api/onshape/download-gltf", {
                        params: {
                            documentId: status.data.resultDocumentId,
                            workspaceId: status.data.resultWorkspaceId,
                            elementId: resultId,
                        },
                        responseType: "blob",
                    });
                    glbBlob = dl.data;
                    break;
                }
                if (status.data.requestState === "FAILED") throw new Error("Export failed");
            }
            if (!glbBlob) throw new Error("Export timeout");

            // ---- success ----
            glbBlobRef.current = glbBlob; // keep for download
            await loadGLTF(glbBlob);
            showNotification("Model updated!", "success");

            // === MOVE TO FINAL STAGE ===
            setModelConfig((p) => ({ ...p, glbReady: true }));

            await generateEcoprotectorDescriptionPdfDirectly(workItemId);
            moveToDownloadStage(); // ← THIS UNLOCKS "Add to CPQ"

            // update state hook so the "download & quote" stage becomes active
            setModelConfig((p) => ({ ...p, glbReady: true }));
            // (the hook already watches `modelConfig.glbReady` to move forward)
        } catch (err) {
            showNotification(err.message || "Update failed", "error");
        } finally {
            setIsGenerating(false);
        }
    }, [
        modelConfig,
        coverHeight,
        clearPreviousModel,
        loadGLTF,
        showNotification,
        saveStateToLocalStorage,
        setModelConfig,
    ]);

    // Add this function inside your component, after the other handlers
    // Add this inside your Dynamics365EcoProtector1.jsx component
    const generateEcoprotectorDescriptionPdfDirectly = useCallback(
        async (workItemId) => {
            // Optional: early exit if critical data is missing (non-blocking)
            if (!QuoteId) {
                showNotification("Quote ID not available – description PDF will be skipped", "warning");
                moveToDownloadStage();
                return;
            }

            try {
                const payload = {
                    ...modelConfig,
                    QuoteId: QuoteId || null,
                    TemplateId: "892243f1-f2b2-f011-bbd3-000d3ad0ff6e",
                    dataverseProductPrice: totalPrice || 0,
                    dataveserQuoteProductQuantity: quantity || 1,
                    workItemId: workItemId || `onshape_${modelConfig.modelType}_${Date.now()}`, // fallback if null
                };

                console.log("Generating EcoProtector description PDF directly...", {
                    workItemId,
                    QuoteId,
                    totalPrice,
                    quantity,
                });

                await apiClient.post("/api/pdf/generate-ecoprotector-description-pdf", payload);

                showNotification("3D model + quote description PDF generated successfully!", "success");
            } catch (err) {
                console.error("Direct PDF generation failed:", err);
                const msg =
                    err.response?.data?.message || "Failed to generate quote PDF (3D model is still ready)";
                showNotification(msg, "warning");
            } finally {
                // Always progress the workflow – PDF is optional
                moveToDownloadStage();
            }
        },
        [QuoteId, modelConfig, totalPrice, quantity, showNotification, moveToDownloadStage]
    );

    // Add this near your other handlers
    const handleFreightPriceOverride = useCallback((newFreightPrice) => {
        setPriceBreakdown((prev) => ({
            ...prev,
            freightPrice: newFreightPrice,
            totalPrice: prev.totalPrice - prev.freightPrice + newFreightPrice,
        }));
    }, []); // empty deps = stable forever

    /* ----------------------  DOWNLOAD GLB  ----------------------- */
    const handleDownloadGLB = useCallback(() => {
        if (!glbBlobRef.current) {
            showNotification("No model to download", "error");
            return;
        }
        const url = URL.createObjectURL(glbBlobRef.current);
        const a = document.createElement("a");
        a.href = url;
        a.download = `ecoprotector_${modelConfig.modelType}.glb`;
        a.click();
        URL.revokeObjectURL(url);
        showNotification("GLB downloaded", "success");
    }, [modelConfig.modelType, showNotification]);

    /* ----------------------  DOWNLOAD PDF  ----------------------- */
    const pollAndDownloadPDF = async (translationId) => {
        for (let i = 0; i < 30; i++) {
            const status = await apiClient.get(`/api/onshape/translation-status/${translationId}`);
            if (status.data.requestState === "DONE") {
                return await apiClient
                    .get("/api/onshape/download-pdf", {
                        params: {
                            documentId: status.data.resultDocumentId,
                            workspaceId: status.data.resultWorkspaceId,
                            elementId: status.data.resultElementIds[0],
                        },
                        responseType: "blob",
                    })
                    .then((r) => r.data);
            }
            if (status.data.requestState === "FAILED") throw new Error(status.data.failureReason);
            await new Promise((r) => setTimeout(r, 2000));
        }
        throw new Error("PDF timeout");
    };

    const handleDownloadPDF = async () => {
        setIsGenerating(true);
        try {
            const createRes = await apiClient.post("/api/onshape/create-drawing");
            const drawingElementId = createRes.data.drawingElementId;

            const exportRes = await apiClient.post("/api/onshape/export-drawing", { drawingElementId });
            const translationId = exportRes.data.translationId;

            const pdfBlob = await pollAndDownloadPDF(translationId);
            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `ecoprotector_drawing_${modelConfig.modelType}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            showNotification("PDF downloaded", "success");
        } catch {
            showNotification("PDF generation failed", "error");
        } finally {
            setIsGenerating(false);
        }
    };

    /* ----------------------  ADD TO CPQ  ------------------------ */
    const addToCPQQuote = async () => {
        setIsAddingToQuote(true);
        if (!totalPrice || totalPrice <= 0) {
            showNotification("Calculate price first", "error");
            setIsAddingToQuote(false);
            return;
        }

        try {
            const detailDescription = [
                `Model: ${modelConfig.modelType}`,
                `FSL: ${modelConfig.finishSurfaceLevel}m`,
                `Inlet IL: ${modelConfig.inletIL}m`,
                `Cover: ${modelConfig.coverType}`,
                `Freight: ${modelConfig.city}`,
                `Riser: ${modelConfig.riserHeight}mm`,
                modelConfig.connectionSize && `Conn. Size: ${modelConfig.connectionSize}mm`,
                modelConfig.connectionType && `Conn. Type: ${modelConfig.connectionType}`,
            ]
                .filter(Boolean)
                .join(" | ");

            const productData = {
                quoteId: QuoteId || null,
                workItemId: modelConfig.workItemId || null, // ← EXPLICIT ROOT LEVEL
                productType: "EcoProtector",
                modelConfig: {
                    ...modelConfig,
                    configurationSummary: `${modelConfig.modelType} - ${modelConfig.coverType} - ${modelConfig.city}`,
                },
                priceBreakdown: priceBreakdown ? { ...priceBreakdown } : null,
                totalPrice,
                productName: `EcoProtector ${modelConfig.modelType}`,
                partNumber: modelConfig.modelType,
                pcs_DetailDescriptions: detailDescription,
                quantity,
                price: totalPrice,
            };

            addProduct(productData);
            showNotification(`EcoProtector ${modelConfig.modelType} added to CPQ!`, "success");
            setTimeout(() => {
                resetWorkflow();
                setIsAddingToQuote(false);
            }, 1500);
        } catch (e) {
            showNotification(`Add failed: ${e.message}`, "error");
            setIsAddingToQuote(false);
        }
    };

    /* ----------------------  INITIALISATION  -------------------- */
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get("id");
        if (id) setQuoteId(id);

        if (!state.isAuthenticated) {
            navigate("/login", { state: { returnTo: location.pathname + location.search } });
        } else {
            const restored = restoreStateFromLocalStorage();
            if (restored) showNotification("Session restored", "info");
        }
    }, [location, state.isAuthenticated, navigate, restoreStateFromLocalStorage, showNotification]);

    useEffect(() => {
        loadConnectionSizes(modelConfig.modelType);
    }, [loadConnectionSizes, modelConfig.modelType]);

    /* ----------------------  INITIAL MODEL LOAD  ---------------- */
    /* ----------------------  INITIAL DEFAULT MODEL LOAD  ---------------- */
    useEffect(() => {
        const loadDefaultModel = async () => {
            if (modelRef.current || modelConfig.glbReady) return;

            try {
                const response = await fetch("/models/EcoProtectorDefault.gltf");
                if (!response.ok) throw new Error("Failed to load default model");
                const blob = await response.blob();
                glbBlobRef.current = blob;
                await loadGLTF(blob);
                showNotification("Default model loaded", "info");
            } catch (err) {
                console.error("Default model load failed:", err);
                showNotification("Failed to load default model", "warning");
            }
        };

        loadDefaultModel();
    }, [loadGLTF, showNotification, modelConfig.glbReady]);

    /* ----------------------  INPUT HANDLERS  -------------------- */
    const handleModelTypeChange = async (e) => {
        const v = e.target.value;
        setModelConfig((p) => ({ ...p, modelType: v }));
        await loadConnectionSizes(v);
    };
    const handleConnectionSizeChange = async (e) => {
        const v = e.target.value;
        setModelConfig((p) => ({ ...p, connectionSize: v }));
        await loadConnectionTypes(v);
    };
    const handleConfigChange = (updates) => setModelConfig((p) => ({ ...p, ...updates }));

    /* ---------------------------------------------------------------- */
    return (
        <Container>
            <Dynamics365NavigationBar />

            <Paper sx={{ p: 2, mb: 3 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {STAGE_LABELS.map((l, i) => (
                        <Step key={l} completed={activeStep > i}>
                            <StepLabel>{l}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Box sx={{ mt: 2, display: "flex", gap: 2, justifyContent: "center" }}>
                    {isGenerating && (
                        <Typography variant="caption" color="info.main">
                            Generating model…
                        </Typography>
                    )}
                </Box>

                {currentStage !== WORKFLOW_STAGES.CALCULATE_PRICE && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={resetWorkflow}
                            disabled={isGenerating || isCalculatingPrice || isAddingToQuote}
                        >
                            New Configuration
                        </Button>
                    </Box>
                )}
            </Paper>

            <Grid container spacing={2}>
                {/* ---------- LEFT PANEL (CONFIG) ---------- */}
                <Grid item xs={4}>
                    <Paper sx={{ p: 3, mt: 3, elevation: 4 }}>
                        <Fade in>
                            <Box>
                                {/* Model type */}
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Model Type</InputLabel>
                                    <Select
                                        value={modelConfig.modelType}
                                        onChange={handleModelTypeChange}
                                        disabled={!canInteractWithInputs()}
                                    >
                                        {modelTypes.map((t) => (
                                            <MenuItem key={t} value={t}>
                                                {t}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* FSL / Inlet IL */}
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            margin="normal"
                                            type="number"
                                            label="Finish Surface Level (m)"
                                            value={modelConfig.finishSurfaceLevel}
                                            onChange={(e) =>
                                                handleConfigChange({
                                                    finishSurfaceLevel: parseFloat(e.target.value),
                                                })
                                            }
                                            error={!!errors.fslAndInletIL}
                                            disabled={!canInteractWithInputs()}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            margin="normal"
                                            type="number"
                                            label="Inlet IL (m)"
                                            value={modelConfig.inletIL}
                                            onChange={(e) =>
                                                handleConfigChange({ inletIL: parseFloat(e.target.value) })
                                            }
                                            error={!!errors.fslAndInletIL}
                                            disabled={!canInteractWithInputs()}
                                        />
                                    </Grid>
                                </Grid>
                                {errors.fslAndInletIL && (
                                    <Typography
                                        color="error"
                                        variant="caption"
                                        sx={{ mt: 1, display: "block" }}
                                    >
                                        {errors.fslAndInletIL}
                                    </Typography>
                                )}

                                {/* Connection size */}
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Connection Size (mm)</InputLabel>
                                    <Select
                                        value={modelConfig.connectionSize || ""}
                                        onChange={handleConnectionSizeChange}
                                        disabled={connectionSizes.length === 0 || !canInteractWithInputs()}
                                    >
                                        {connectionSizes.length === 0 ? (
                                            <MenuItem disabled>Loading…</MenuItem>
                                        ) : (
                                            connectionSizes.map((s) => (
                                                <MenuItem key={s} value={s}>
                                                    {s}
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>

                                {/* Connection type + Cover */}
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel>Connection Type</InputLabel>
                                            <Select
                                                value={modelConfig.connectionType || ""}
                                                onChange={(e) =>
                                                    handleConfigChange({ connectionType: e.target.value })
                                                }
                                                disabled={
                                                    connectionTypes.length === 0 || !canInteractWithInputs()
                                                }
                                            >
                                                {connectionTypes.length === 0 ? (
                                                    <MenuItem disabled>Loading…</MenuItem>
                                                ) : (
                                                    connectionTypes.map((t) => (
                                                        <MenuItem key={t} value={t}>
                                                            {t}
                                                        </MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel>Cover Type</InputLabel>
                                            <Select
                                                value={modelConfig.coverType}
                                                onChange={(e) =>
                                                    handleConfigChange({ coverType: e.target.value })
                                                }
                                                disabled={!canInteractWithInputs()}
                                            >
                                                {coverTypes.map((t) => (
                                                    <MenuItem key={t} value={t}>
                                                        {t}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                {/* Destination address */}
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body1" color="text.secondary">
                                        Destination Address
                                    </Typography>
                                    <AddressAutocomplete onAddressSelect={setModelConfig} />
                                </Box>

                                {/* Calculate price */}
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={calculatePrice}
                                    disabled={!canCalculatePrice()}
                                    sx={{ mt: 2 }}
                                >
                                    {isCalculatingPrice ? <CircularProgress size={24} /> : "Calculate Price"}
                                </Button>

                                <FreightCalculationDisplayModelView
                                    calculationResult={freightCalculationResult}
                                    onFreightPriceOverride={handleFreightPriceOverride}
                                />

                                <PriceBreakdown priceData={priceBreakdown} />

                                {/* UPDATE / GENERATE */}
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleUpdate}
                                    disabled={!canGenerateModel()}
                                    sx={{ mt: 2 }}
                                    startIcon={isGenerating ? <CircularProgress size={20} /> : null}
                                >
                                    {isGenerating ? "Updating…" : "Update & Preview 3D"}
                                </Button>

                                {/* Download GLB */}
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={handleDownloadGLB}
                                    disabled={!canDownloadAndQuote() || !modelConfig.glbReady}
                                    sx={{ mt: 1 }}
                                >
                                    Download GLB
                                </Button>

                                {/* Download PDF */}
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="success"
                                    onClick={handleDownloadPDF}
                                    disabled={!canDownloadAndQuote() || isGenerating}
                                    sx={{ mt: 1 }}
                                    startIcon={isGenerating ? <CircularProgress size={20} /> : null}
                                >
                                    {isGenerating ? "Processing…" : "Download PDF"}
                                </Button>
                            </Box>
                        </Fade>
                    </Paper>

                    {/* QUANTITY + ADD TO CPQ */}
                    <Paper sx={{ p: 3, mt: 3, elevation: 4 }}>
                        <TextField
                            label="Product Quantity"
                            type="number"
                            value={quantity}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (v === "" || (/^\d+$/.test(v) && parseInt(v) > 0)) {
                                    setQuantity(v === "" ? 1 : parseInt(v));
                                }
                            }}
                            disabled={!canDownloadAndQuote()}
                            variant="outlined"
                            size="small"
                            inputProps={{ min: 1 }}
                            sx={{ width: 140, mt: 2 }}
                            error={quantity < 1}
                            helperText={quantity < 1 ? "≥ 1" : ""}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            color="secondary"
                            onClick={addToCPQQuote}
                            disabled={!canDownloadAndQuote() || isAddingToQuote || quantity < 1}
                            sx={{ mt: 2 }}
                        >
                            {isAddingToQuote ? (
                                <CircularProgress size={24} />
                            ) : (
                                `Add to CPQ – $${totalPrice?.toLocaleString() || "0"}`
                            )}
                        </Button>
                    </Paper>
                </Grid>

                {/* ---------- RIGHT PANEL (3D VIEWER) ---------- */}
                <Grid item xs={8}>
                    <Paper sx={{ p: 2, height: "85vh", position: "relative" }}>
                        <Box ref={mountRef} sx={{ width: "100%", height: "100%" }} />
                        {isGenerating && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    bgcolor: "rgba(255,255,255,0.85)",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <CircularProgress size={60} />
                                <Typography sx={{ mt: 2 }}>Generating 3D model…</Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            <Box sx={{ mt: 3, mb: 2 }}>
                <BackToCPQButton />
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Dynamics365EcoProtector1;
